export const ISO_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const ISO_DATETIME_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

export function isNullOrUndefined<T>(value: T) {
  return value === undefined || value === null;
}

export function deleteNullProperties<T>(object: T) {
  return Object.fromEntries(Object.entries(object).filter(([_, v]) => v !== null && v !== '' && v !== undefined)) as T
}

export function deepClone<T>(object: T) {
  return JSON.parse(JSON.stringify(object)) as T;
}
export function convertirColor(value: number | string | undefined) {
  if (value !== undefined) {
    let color = ('00000' + value.toString(16)).slice(-6);
    color = color.slice(-2) + color.slice(2, 4) + color.slice(0, 2);
    return '#' + color;
  } else {
    return '#000000';
  }
}

export function sameObject(a: Object, b: Object) {
  return JSON.stringify(a) === JSON.stringify(b);
}
export const isObject = (object) => {
  return object != null && typeof object === 'object';
}
export function deepEqual(object1: unknown, object2: unknown): boolean {
  if (object1 === object2) return true;
  if (object1 === null || object1 === undefined || object2 === null || object2 === undefined) return false;
  if (!isObject(object1) || !isObject(object2)) return object1 === object2;

  const first = object1 as Record<string, unknown>;
  const second = object2 as Record<string, unknown>;
  const keys1 = Object.keys(first);
  const keys2 = Object.keys(second);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = first[key];
    const val2 = second[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      areObjects && !deepEqual(val1, val2) ||
      !areObjects && val1 !== val2
    ) {
      return false;
    }
  }
  return true;
}

// ==== Utilidades de fechas (tus fechas vienen como 'YYYY-MM-DD' o '-') ====
export const parseDate = (s?: string): Date | null => {
  if (!s || s === '-') return null;
  const d = new Date(s);
  return isNaN(+d) ? null : d;
};

export const inRange = (d: Date | null, range: Date[] | null | undefined): boolean => {
  if (!range?.length) return true;                        // sin filtro
  if (!d) return false;                                   // no hay fecha en la fila -> no pasa
  const [start, end] = range;
  if (start && d < start) return false;
  return !(end && d > end);

}
export const includesIC = (haystack: string, needle: string): boolean => {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export const toIsoFromEsDate = (s?: string | null): string | null => {
  if (!s) return null;
  // admitimos "10/6/2025, 11:32:25" o "10/6/2025 11:32:25"
  const [datePart, timePartRaw] = s.split(',').map(x => x?.trim()) as [string, string?];
  if (!datePart) return null;
  const [dd, mm, yyyy] = datePart.split('/').map(n => parseInt(n, 10));
  const [HH = 0, MM = 0, SS = 0] = (timePartRaw ?? '').split(':').map(n => parseInt(n, 10));
  if (!yyyy || !mm || !dd) return null;
  // Generamos ISO en UTC para que el pipe con 'UTC' del hijo muestre la misma hora
  return new Date(Date.UTC(yyyy, mm - 1, dd, HH || 0, MM || 0, SS || 0)).toISOString();
}

export const toDateOnly = (d: Date): Date => {
  const x = new Date(d.getTime());
  x.setHours(0, 0, 0, 0);
  return x;
}

export const isValidDateObject = (d: Date): boolean => {
  return d instanceof Date && !isNaN(d.getTime());
}

export const parseISODate = (value: string | Date): Date | null => {


  if (value instanceof Date) {
    return isValidDateObject(value) ? toDateOnly(value) : null;
  }
  if (typeof value === 'string') {
    const fecha = new Date(value.replace(" ", "T"));
    if (value.length >= 11 && isValidDateObject(fecha)) return toDateOnly(fecha);

    if (!ISO_DATE_RE.test(value)) return null;
    const [y, m, day] = value.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, day));
    // validar que la fecha exista (p.ej. 2025-02-30 no)
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== day) return null;
    // normalizamos a local día 00:00 para comparaciones de “fecha” (sin hora)
    return toDateOnly(new Date(y, m - 1, day));
  }
  return null;
}

export const parseISODateTime = (value: string | Date): Date | null => {
  if (value instanceof Date) {
    return isValidDateObject(value) ? value : null;
  }
  if (typeof value === 'string') {
    const m = ISO_DATETIME_RE.exec(value);
    if (!m) return null;
    const [y, mo, d, hh, mm, ss] = [
      Number(value.slice(0, 4)),
      Number(value.slice(5, 7)),
      Number(value.slice(8, 10)),
      Number(value.slice(11, 13)),
      Number(value.slice(14, 16)),
      m[5] ? Number(m[5]) : 0,
    ];
    const dt = new Date(y, mo - 1, d, hh, mm, ss, 0);
    return isValidDateObject(dt) ? dt : null;
  }
  return null;
}

export const fmt = (val: string | Date): string => {
  if (val instanceof Date) {
    // YYYY-MM-DD HH:mm:ss
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(val.getDate())} ${pad(val.getHours())}:${pad(val.getMinutes())}:${pad(val.getSeconds())}`;
  }
  return val;
}

export const uniq = <T>(arr: T[]): T[] => Array.from(new Set(arr));


export const parseDateDDMMYYYY = (value: any): number | null => {
  if (!value) return null;
  const str = String(value).trim();
  if (str === '-' || str === '1/1/1970') return null;

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(str);
  if (!match) return null;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  let year = parseInt(match[3], 10);
  if (year < 100) year = 2000 + year; // por si acaso

  const d = new Date(year, month - 1, day);
  const t = d.getTime();
  return isNaN(t) ? null : t;
}


export const bytesToMegaOrGiga = (bytes: number): string => {
  const MB = 1024 * 1024;
  const GB = 1024 * 1024 * 1024;

  if (bytes >= GB) {
    return (bytes / GB).toFixed(2) + " GB";
  } else {
    return (bytes / MB).toFixed(2) + " MB";
  }
}
